/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import PropTypes from 'prop-types';
import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { ButtonIcon } from '..';
import { randomString } from "../../utils";
import SliderItem from "./slider-item";
import { useColor as colors } from "../../theme";
import { itemsCenter, bgTransparent, boxBorder, cursorPointer, displayNone, flexCol, flexRow, justifyCenter, overflowHidden, positionAbsolute, positionRelative, userSelectNone } from "../../styles/general";
const {
  system: {
    active,
    rest,
    white
  },
  fill: {
    pressed: fillPressed
  }
} = colors;
const styles = {
  iconLeft: {
    left: 0,
    position: 'absolute',
    top: '50%',
    transform: 'translate(0, -50%)'
  },
  iconRight: {
    right: 0,
    position: 'absolute',
    top: '50%',
    transform: 'translate(0, -50%)'
  }
};
const SliderContainer = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  animation,
  active,
  autoChangeTime,
  children,
  className,
  data,
  onShowNavigation,
  style
}, reference) => {
  let autoChange;
  let currentItemIndex = 0;
  const Id = randomString(7, {
    allowSymbol: false,
    allowNumber: false
  });
  const IDs = {
    container: 'DGN-UI-Slider-container-' + Id,
    icon: 'DGN-UI-Slider-icon',
    iconLeft: 'DGN-UI-Slider-icon-left',
    iconRight: 'DGN-UI-Slider-icon-right',
    navigation: 'DGN-UI-Slider-navigation-' + Id,
    navigationList: 'DGN-UI-Slider-navigation-list-' + Id,
    navigationItem: 'DGN-UI-Slider-navigation-item',
    slideContainer: 'DGN-UI-Slider-slide-container'
  };
  const ref = useRef(null);
  const slideContainerRef = useRef(null);
  const navigationRef = useRef(null);
  const navigationListRef = useRef(null);
  const iconLeftRef = useRef(null);
  const iconRightRef = useRef(null);
  const _containerCSS = containerCSS(IDs);
  const _navigationListCSS = navigationListCSS(IDs);
  const removeClass = (className, selector) => {
    const array = document.querySelectorAll(`.${selector}`) || document.querySelectorAll(selector);
    array.forEach(a => {
      a.classList.remove(className);
    });
  };
  const onChangeActiveItem = (childNodeList, e, i) => {
    removeClass('item-focused', IDs.navigationItem);
    e.classList.add('item-focused');
    childNodeList.forEach(node => {
      node.style.display = 'none';
    });
    childNodeList[i].style.display = 'block';
    currentItemIndex = i;
  };
  const onNextItem = childNodeList => {
    if (childNodeList.length > 1 && autoChangeTime && autoChangeTime > 0) {
      clearInterval(autoChange);
      autoChange = setInterval(() => onNextItem(childNodeList), autoChangeTime * 1000);
    }
    childNodeList[currentItemIndex].style.display = 'none';
    if (currentItemIndex === childNodeList.length - 1) {
      currentItemIndex = 0;
    } else {
      currentItemIndex += 1;
    }
    childNodeList[currentItemIndex].style.display = 'block';
    onChangeActiveItem(childNodeList, navigationListRef.current.childNodes[currentItemIndex], currentItemIndex);
  };
  const onPrevItem = childNodeList => {
    if (childNodeList.length > 1 && autoChangeTime && autoChangeTime > 0) {
      clearInterval(autoChange);
      autoChange = setInterval(() => onNextItem(childNodeList), autoChangeTime * 1000);
    }
    childNodeList[currentItemIndex].style.display = 'none';
    if (currentItemIndex === 0) {
      currentItemIndex = childNodeList.length - 1;
    } else {
      currentItemIndex -= 1;
    }
    childNodeList[currentItemIndex].style.display = 'block';
    onChangeActiveItem(childNodeList, navigationListRef.current.childNodes[currentItemIndex], currentItemIndex);
  };
  const findActiveItem = childNodeList => {
    for (let i = 0; i < childNodeList.length; i++) {
      if (childNodeList[i].classList.contains('active')) {
        childNodeList[i].style.display = 'block';
        currentItemIndex = i;
        return i;
      }
    }
    childNodeList[0].style.display = 'block';
    currentItemIndex = 0;
    return 0;
  };
  const renderNavigationItem = childNodeList => {
    const list = navigationListRef.current;
    const activeItem = findActiveItem(childNodeList);
    let items = '';
    for (let i = 0; i < childNodeList.length; i++) {
      items += `<span class='${i === activeItem ? IDs.navigationItem + ' ' + 'item-focused' : IDs.navigationItem}' id='${IDs.navigationItem}-${i}'></span>`;
    }
    list.innerHTML = items;
    navigationListRef.current.querySelectorAll(`.${IDs.navigationItem}`).forEach((item, i) => {
      item.addEventListener('click', e => {
        onChangeActiveItem(childNodeList, e.target, i);
      });
    });
  };
  useEffect(() => {
    if (!slideContainerRef.current || !navigationRef.current || !iconLeftRef.current || !iconRightRef.current) return;
    const childNodeList = slideContainerRef.current.childNodes,
      navigation = navigationRef.current,
      iconLeft = iconLeftRef.current,
      iconRight = iconRightRef.current;
    renderNavigationItem(childNodeList);
    if (childNodeList.length > 1) {
      navigation.style.display = onShowNavigation ? 'block' : 'none';
      iconLeft.style.display = 'block';
      iconRight.style.display = 'block';
      iconRight.addEventListener('click', () => onNextItem(childNodeList));
      iconLeft.addEventListener('click', () => onPrevItem(childNodeList));
    } else {
      navigation.style.display = 'none';
      iconLeft.style.display = 'none';
      iconRight.style.display = 'none';
    }
    return () => {
      childNodeList.forEach(node => {
        node.style.display = 'none';
      });
      if (childNodeList.length > 1) {
        iconRight.removeEventListener('click', () => onNextItem(childNodeList));
        iconLeft.removeEventListener('click', () => onPrevItem(childNodeList));
      }
    };
  });
  useEffect(() => {
    if (!slideContainerRef.current) return;
    const childNodeList = slideContainerRef.current.childNodes;
    if (childNodeList.length > 1 && autoChangeTime && autoChangeTime > 0) {
      autoChange = setInterval(() => onNextItem(childNodeList), autoChangeTime * 1000);
    }
    return () => {
      if (childNodeList.length > 1 && autoChangeTime && autoChangeTime > 0) {
        clearInterval(autoChange);
      }
    };
  }, [autoChangeTime, children, data]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    const _instance = {}; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const Slider = useMemo(() => jsx("div", {
    className: IDs.slideContainer,
    ref: slideContainerRef
  }, children || data && data.map((i, index) => {
    return jsx(SliderItem, {
      animation: animation,
      key: index,
      fileName: i.name,
      active: index === active,
      id: index,
      url: i.url
    });
  })), [animation, autoChangeTime, children, data]);
  const IconLeft = useMemo(() => jsx("span", {
    className: [IDs.icon, IDs.iconLeft].join(' ').trim().replace(/\s+/g, ' '),
    ref: iconLeftRef
  }, jsx(ButtonIcon, {
    circular: true,
    viewType: 'filled',
    color: white,
    name: 'ArrowLeft',
    style: styles.iconLeft,
    viewBox: false
  })), [children, data]);
  const IconRight = useMemo(() => jsx("span", {
    className: [IDs.icon, IDs.iconRight].join(' ').trim().replace(/\s+/g, ' '),
    ref: iconRightRef
  }, jsx(ButtonIcon, {
    circular: true,
    viewType: 'filled',
    color: white,
    name: 'ArrowRight',
    style: styles.iconRight,
    viewBox: false
  })), [children, data]);
  const Navigation = useMemo(() => jsx("div", {
    className: [IDs.navigation, onShowNavigation && 'navigation-hidden'].join(' ').trim().replace(/\s+/g, ' '),
    css: navigationCSS,
    id: IDs.navigation,
    ref: navigationRef
  }, jsx("div", {
    css: _navigationListCSS,
    className: IDs.navigationList,
    id: IDs.navigationList,
    ref: navigationListRef
  })), [animation, autoChangeTime, children, data, onShowNavigation]);
  return jsx("div", {
    css: _containerCSS,
    className: [IDs.container, className].join(' ').trim().replace(/\s+/g, ' '),
    id: IDs.container,
    style: style,
    ref: ref
  }, Slider, IconLeft, IconRight, Navigation);
}));
const containerCSS = IDs => css`
    * {
        ${boxBorder};
        margin: 0;
        padding: 0;
    }
    ${flexCol};
    ${itemsCenter};
    ${justifyCenter};
    ${positionRelative};
    ${bgTransparent};
    height: 100%;
    resize: both;
    .${IDs.icon} {
        opacity: 0;
        &.${IDs.iconLeft} {
            ${displayNone};
            ${cursorPointer};
            ${userSelectNone};
            ${positionAbsolute};
            height: 100%;
            left: 0;
            margin-left: 16px;
            width: 15%;
        }
        &.${IDs.iconRight} {
            ${displayNone};
            ${cursorPointer};
            ${userSelectNone};
            ${positionAbsolute};
            height: 100%;
            right: 0;
            margin-right: 16px;
            width: 15%;
        }
    }
    .${IDs.slideContainer} {
        ${overflowHidden};
        ${positionRelative};
        ${bgTransparent};
        height: 95%;
        margin: 0 auto;
        width: 100%;
    }
    &:hover {
        .${IDs.icon} {
            opacity: 1;
        }
    }
`;
const navigationCSS = css`
    ${displayNone};
    ${positionRelative};
    background-color: ${white};
    height: 5%;
    width: 100%;
    &.navigation-hidden {
        ${displayNone};
    }
`;
const navigationListCSS = IDs => css`
    ${flexRow};
    ${positionAbsolute};
    left: 50%;
    top: 50%;
    transform: translate(-50%, 14px);
    .${IDs.navigationItem} {
        ${cursorPointer};
        ${positionRelative};
        background-color: ${rest};
        border-radius: 50%;
        height: 12px;
        margin: 0 6px;
        width: 12px;
        :hover:not(.item-focused) {
            background-color: ${fillPressed};
        }
        &.item-focused {
            background-color: ${active};
        }
    }
`;
SliderContainer.defaultProps = {
  animation: true,
  autoChangeTime: 0,
  children: null,
  data: [],
  onShowNavigation: true,
  className: '',
  style: {}
};
SliderContainer.propTypes = {
  /** Index of the active item. */
  active: PropTypes.number,
  /** Enable/disable the animation when change active item. */
  animation: PropTypes.bool,
  /** Time to slider auto change (s). */
  autoChangeTime: PropTypes.number,
  /** Element or node to display on slider without data.  */
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.node]),
  /** Class for component. */
  className: PropTypes.string,
  /** Data to display on slider. */
  data: PropTypes.array,
  /** Show/hide the navigation bar. */
  onShowNavigation: PropTypes.bool,
  /** Style inline of component. */
  style: PropTypes.object
};
export default SliderContainer;